import { io, type Socket } from 'socket.io-client';
import {
  SOCKET_NAMESPACE,
  type Ack,
  type ClientToServerEvents,
  type ServerToClientEvents,
} from '@anon-chat/shared';
import { getAccessToken } from './api';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL ?? window.location.origin;

export type ChatSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: ChatSocket | null = null;

/**
 * Одно соединение на всё приложение. Только WebSocket: long-polling запрещён
 * требованиями, а в Telegram WebApp он ещё и хуже переживает сворачивание.
 */
export function connectSocket(): ChatSocket {
  if (socket) {
    return socket;
  }

  socket = io(`${SOCKET_URL}${SOCKET_NAMESPACE}`, {
    transports: ['websocket'],
    autoConnect: true,
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 5000,
    // Токен читается функцией: после обновления пары переподключение
    // использует свежий access-токен, а не тот, что был при первом вызове.
    auth: (cb) => cb({ token: getAccessToken() ?? '' }),
  }) as ChatSocket;

  return socket;
}

export function getSocket(): ChatSocket | null {
  return socket;
}

/**
 * Переподключение под новыми токенами.
 *
 * Пользователь привязывается к сокету в момент рукопожатия, и после входа
 * посреди разговора соединение осталось бы гостевым: человек продолжал бы
 * говорить от лица записи, у которой уже нет ни участия в чате, ни права на
 * дружбу. Токен читается заново при каждом подключении — достаточно разорвать
 * и поднять.
 */
export function reconnectSocket(): void {
  if (!socket) {
    return;
  }
  socket.disconnect();
  socket.connect();
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}

/** Промис-обёртка над emit с подтверждением. */
export function emitWithAck<T>(
  event: keyof ClientToServerEvents,
  payload?: unknown,
): Promise<Ack<T>> {
  return new Promise((resolve) => {
    const current = socket;
    if (!current?.connected) {
      resolve({
        ok: false,
        error: { code: 'INTERNAL_ERROR', message: 'Нет соединения' },
      } as Ack<T>);
      return;
    }

    const handler = (response: Ack<T>) => resolve(response);
    if (payload === undefined) {
      (current.emit as (e: string, ack: unknown) => void)(event, handler);
    } else {
      (current.emit as (e: string, p: unknown, ack: unknown) => void)(event, payload, handler);
    }
  });
}
